// Shared text generators for all curriculum trins.

import type {
  GenerateRoundOptions,
  Niveau4Lexicon,
  PracticeMode,
  RoundPresentation,
  RoundTemplate,
} from '../../types';

export function resolveNiveau4Lexicon(
  opts: GenerateRoundOptions,
): Niveau4Lexicon {
  return opts.niveau4Lexicon === 'en' ? 'en' : 'da';
}

export function localizedLiteral(da: string, en: string) {
  return (opts: GenerateRoundOptions) =>
    resolveNiveau4Lexicon(opts) === 'en' ? en : da;
}

export function localizedFromChunks(
  da: string[],
  en: string[],
  length: number,
  options: FromChunksOptions = {},
) {
  const daGen = fromChunks(da, length, options);
  const enGen = fromChunks(en, length, options);
  return (opts: GenerateRoundOptions) =>
    resolveNiveau4Lexicon(opts) === 'en' ? enGen(opts) : daGen(opts);
}

export function localizedInterleaved(
  daSources: Array<{ chunks: string[]; weight?: number }>,
  enSources: Array<{ chunks: string[]; weight?: number }>,
  length: number,
) {
  const daGen = interleaved(daSources, length);
  const enGen = interleaved(enSources, length);
  return (opts: GenerateRoundOptions) =>
    resolveNiveau4Lexicon(opts) === 'en' ? enGen(opts) : daGen(opts);
}

export const DEFAULT_ROUND_MS = 60_000;

export function makeRng(seed?: number): () => number {
  if (seed === undefined) return Math.random;
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

const ERROR_BIAS_MAX = 3;
const SLOW_BIAS = 1;

export function weightedPickKey(
  keys: string[],
  weak: Record<string, number> | undefined,
  slow: Record<string, number> | undefined,
  rng: () => number,
): string {
  if (keys.length === 0) return '';
  const errors = weak ?? {};
  const slowKeys = slow ?? {};
  let totalErrors = 0;
  for (const k of keys) totalErrors += errors[k] ?? 0;
  let total = 0;
  const weights = new Array<number>(keys.length);
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    const errShare = totalErrors > 0 ? (errors[k] ?? 0) / totalErrors : 0;
    const w = 1 + ERROR_BIAS_MAX * errShare + (slowKeys[k] ? SLOW_BIAS : 0);
    weights[i] = w;
    total += w;
  }
  let r = rng() * total;
  for (let i = 0; i < keys.length; i++) {
    r -= weights[i];
    if (r <= 0) return keys[i];
  }
  return keys[keys.length - 1];
}

function scoreChunk(
  chunk: string,
  weak: Record<string, number> | undefined,
  slow: Record<string, number> | undefined,
  baseScore = 1,
): number {
  let score = baseScore;
  const seen = new Set<string>();
  for (const ch of chunk) {
    if (seen.has(ch)) continue;
    seen.add(ch);
    score += weak?.[ch] ?? 0;
    if (slow?.[ch]) score += 1;
  }
  return score;
}

function pickWeightedChunk(
  scored: Array<{ c: string; score: number }>,
  rng: () => number,
): string {
  let total = 0;
  for (const s of scored) total += s.score;
  let r = rng() * total;
  for (const s of scored) {
    r -= s.score;
    if (r <= 0) return s.c;
  }
  return scored[scored.length - 1]!.c;
}

export function chunkText(
  chunks: string[],
  length: number,
  weak: Record<string, number> | undefined,
  slow: Record<string, number> | undefined,
  rng: () => number,
): string {
  if (chunks.length === 0) return '';
  const scored = chunks.map((c) => ({ c, score: scoreChunk(c, weak, slow) }));
  const out: string[] = [];
  let chars = 0;
  while (chars < length) {
    const pick = pickWeightedChunk(scored, rng);
    out.push(pick);
    chars += pick.length + 1;
  }
  return out.join(' ').trim();
}

/** Like chunkText, but each chunk appears at most `maxRepeat` times before the pool resets. */
export function chunkTextLimited(
  chunks: string[],
  length: number,
  maxRepeat: number,
  weak: Record<string, number> | undefined,
  slow: Record<string, number> | undefined,
  rng: () => number,
): string {
  if (chunks.length === 0) return '';
  const scored = chunks.map((c) => ({ c, score: scoreChunk(c, weak, slow) }));
  const counts = new Map<string, number>();
  const out: string[] = [];
  let chars = 0;
  while (chars < length) {
    let candidates = scored.filter((s) => (counts.get(s.c) ?? 0) < maxRepeat);
    if (candidates.length === 0) {
      counts.clear();
      candidates = scored;
    }
    const pick = pickWeightedChunk(candidates, rng);
    out.push(pick);
    counts.set(pick, (counts.get(pick) ?? 0) + 1);
    chars += pick.length + 1;
  }
  return out.join(' ').trim();
}

export function literal(text: string) {
  return (_opts: GenerateRoundOptions) => text;
}

export function literalOneOf(texts: readonly string[]) {
  return ({ seed }: GenerateRoundOptions) => {
    if (texts.length === 0) return '';
    const rng = makeRng(seed);
    return texts[Math.floor(rng() * texts.length)]!;
  };
}

export interface FromChunksOptions {
  /** Max times the same chunk may appear before the pool resets (default: unlimited). */
  maxRepeatPerChunk?: number;
}

export function fromChunks(
  chunks: string[],
  length: number,
  options: FromChunksOptions = {},
) {
  const maxRepeat = options.maxRepeatPerChunk;
  return ({ weakKeys, slowKeys, seed }: GenerateRoundOptions) => {
    const rng = makeRng(seed);
    if (maxRepeat === undefined) {
      return chunkText(chunks, length, weakKeys, slowKeys, rng);
    }
    return chunkTextLimited(
      chunks,
      length,
      maxRepeat,
      weakKeys,
      slowKeys,
      rng,
    );
  };
}

/**
 * Mix chunks from multiple sources in one round (interleaved review).
 *
 *   - Each source has an optional `weight` (default 1). Higher weight means
 *     its chunks are picked relatively more often.
 *   - Within a source, the usual weak/slow bias still applies.
 *   - Use this for "bland med tidligere niveauer"-runder where we want to
 *     keep already-mastered tiers fresh while drilling the newest tier.
 */
export function interleaved(
  sources: Array<{ chunks: string[]; weight?: number }>,
  length: number,
) {
  return ({ weakKeys, slowKeys, seed }: GenerateRoundOptions) => {
    const rng = makeRng(seed);
    const pool: Array<{ c: string; score: number }> = [];
    for (const src of sources) {
      const w = src.weight ?? 1;
      for (const chunk of src.chunks) {
        let score = w;
        const seen = new Set<string>();
        for (const ch of chunk) {
          if (seen.has(ch)) continue;
          seen.add(ch);
          score += weakKeys?.[ch] ?? 0;
          if (slowKeys?.[ch]) score += 1;
        }
        pool.push({ c: chunk, score });
      }
    }
    if (pool.length === 0) return '';
    let total = 0;
    for (const s of pool) total += s.score;
    const out: string[] = [];
    let chars = 0;
    while (chars < length) {
      let r = rng() * total;
      let pick = pool[0].c;
      for (const s of pool) {
        r -= s.score;
        if (r <= 0) {
          pick = s.c;
          break;
        }
      }
      out.push(pick);
      chars += pick.length + 1;
    }
    return out.join(' ').trim();
  };
}

/** ~`advancedShare` of target length from punctuation/case chunks; rest from `wordSources`. */
export function warmupBlended(
  wordSources: Array<{ chunks: string[]; weight?: number }>,
  advancedChunks: string[],
  length: number,
  advancedShare = 0.2,
) {
  return (opts: GenerateRoundOptions) => {
    if (advancedChunks.length === 0 || advancedShare <= 0) {
      return interleaved(wordSources, length)(opts);
    }
    const share = Math.min(0.5, Math.max(0, advancedShare));
    const advLen = Math.max(12, Math.round(length * share));
    const wordLen = Math.max(1, length - advLen);
    const wordPart = interleaved(wordSources, wordLen)(opts);
    const advPart = interleaved([{ chunks: advancedChunks, weight: 1 }], advLen)(
      opts,
    );
    return [wordPart, advPart].filter(Boolean).join(' ').trim();
  };
}

export function fromLetters(
  letters: string[],
  groupSize: number,
  length: number,
) {
  return ({ weakKeys, slowKeys, seed }: GenerateRoundOptions) => {
    const rng = makeRng(seed);
    const out: string[] = [];
    let chars = 0;
    while (chars < length) {
      let group = '';
      for (let i = 0; i < groupSize; i++) {
        group += weightedPickKey(letters, weakKeys, slowKeys, rng);
      }
      out.push(group);
      chars += groupSize + 1;
    }
    return out.join(' ').trim();
  };
}

/** Keep only words whose letters are all in `allowed`. */
export function filterWords(words: string[], allowed: string[]): string[] {
  const set = new Set(allowed);
  return words.filter((w) => [...w].every((c) => set.has(c)));
}

interface RoundDraft {
  id: string;
  title: string;
  purpose: string;
  mode: PracticeMode;
  generate: (opts: GenerateRoundOptions) => string;
  durationMs?: number;
  presentation?: RoundPresentation;
}

export function round(d: RoundDraft): RoundTemplate {
  return d;
}
